import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ClipboardList, Plus, Trash2, Save, Sparkles, Loader2, CalendarDays,
  ListChecks, AlertTriangle, CheckCircle2, Users, ChevronRight, X, Flag,
  FileText, Bell, User as UserIcon, Link2,
} from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { recordHref } from "../../lib/recordLinks";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { formatDate } from "../../lib/date";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const today = () => new Date().toISOString().slice(0, 10);

const currentUserId = () => {
  try { return (JSON.parse(localStorage.getItem("equipr_session") || "{}") || {}).id || null; }
  catch { return null; }
};

const STATUS = {
  open: { label: "Open", cls: "bg-[#FFF7D1] text-[#6B5600] border-[#FFF7D1]" },
  in_progress: { label: "In progress", cls: "bg-[#FFF8D6] text-[#8A6D00] border-[#FFF8D6]" },
  done: { label: "Done", cls: "bg-[#E3F3E9] text-[#0E6B3F] border-[#E3F3E9]" },
};

const emptyDraft = () => ({
  title: "", meeting_date: today(), attendees: [], raw_notes: "",
  summary: "", decisionsText: "", risksText: "", actions: [],
});

export default function ManagementMeetings() {
  const [tab, setTab] = useState("meetings");
  const [meetings, setMeetings] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [register, setRegister] = useState({ actions: [], total: 0, overdue: 0 });
  const [myOnly, setMyOnly] = useState(false);
  const [agenda, setAgenda] = useState([]);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [templateText, setTemplateText] = useState("");
  const [nudging, setNudging] = useState(false);
  const [linkFor, setLinkFor] = useState(null);
  const [linkQ, setLinkQ] = useState("");
  const [linkResults, setLinkResults] = useState([]);
  const me = currentUserId();

  const persisted = selectedId && selectedId !== "new";

  const loadMeetings = useCallback(async () => {
    setLoading(true);
    try {
      const [m, u, t] = await Promise.all([
        axios.get(`${API}/meetings`),
        axios.get(`${API}/admin/users`).catch(() => ({ data: { users: [] } })),
        axios.get(`${API}/meetings/template`).catch(() => ({ data: { agenda: [] } })),
      ]);
      setMeetings(m.data.meetings || []);
      setUsers(u.data.users || []);
      setAgenda(t.data.agenda || []);
    } catch { toast.error("Failed to load meetings"); }
    finally { setLoading(false); }
  }, []);

  const loadRegister = useCallback(async (mine) => {
    try {
      const params = {};
      if (mine && currentUserId()) params.owner_id = currentUserId();
      const r = await axios.get(`${API}/meetings/open-actions`, { params });
      setRegister(r.data);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { loadMeetings(); loadRegister(false); }, [loadMeetings, loadRegister]);

  const toDraft = (mtg) => ({
    title: mtg.title || "", meeting_date: mtg.meeting_date || today(),
    attendees: mtg.attendees || [], raw_notes: mtg.raw_notes || "",
    summary: mtg.summary || "",
    decisionsText: (mtg.decisions || []).join("\n"),
    risksText: (mtg.risks || []).join("\n"),
    actions: mtg.actions || [],
  });

  const openMeeting = async (id) => {
    setSelectedId(id);
    const r = await axios.get(`${API}/meetings/${id}`);
    setDraft(toDraft(r.data.meeting));
  };
  const newMeeting = () => {
    const d = emptyDraft();
    if (agenda.length) d.raw_notes = agenda.map((a) => `• ${a}: `).join("\n");
    setSelectedId("new"); setDraft(d);
  };

  const saveTemplate = async () => {
    const items = templateText.split("\n").map((s) => s.trim()).filter(Boolean);
    try {
      await axios.put(`${API}/meetings/template`, { agenda: items });
      setAgenda(items); setTemplateOpen(false);
      toast.success("Agenda template saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save template"); }
  };

  const runNudge = async () => {
    setNudging(true);
    try {
      const r = await axios.post(`${API}/meetings/nudge-now`);
      toast.success(`Sent ${r.data.sent} overdue reminder${r.data.sent === 1 ? "" : "s"}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Nudge failed"); }
    finally { setNudging(false); }
  };

  const extract = async () => {
    if (!draft.raw_notes.trim()) return toast.error("Paste the meeting notes first");
    setExtracting(true);
    try {
      const r = await axios.post(`${API}/meetings/extract`, { raw_notes: draft.raw_notes });
      setDraft((d) => ({
        ...d,
        summary: r.data.summary || d.summary,
        decisionsText: (r.data.decisions || []).join("\n"),
        risksText: (r.data.risks || []).join("\n"),
        actions: [...d.actions, ...(r.data.action_items || [])],
      }));
      toast.success("AI extracted the notes — review & edit before saving");
    } catch (e) { toast.error(e.response?.data?.detail || "Extract failed"); }
    finally { setExtracting(false); }
  };

  const meetingPayload = () => ({
    title: draft.title, meeting_date: draft.meeting_date, attendees: draft.attendees,
    raw_notes: draft.raw_notes, summary: draft.summary,
    decisions: draft.decisionsText.split("\n").map((s) => s.trim()).filter(Boolean),
    risks: draft.risksText.split("\n").map((s) => s.trim()).filter(Boolean),
  });

  const save = async () => {
    setSaving(true);
    try {
      if (persisted) {
        await axios.put(`${API}/meetings/${selectedId}`, meetingPayload());
        toast.success("Meeting saved");
        await openMeeting(selectedId);
      } else {
        const r = await axios.post(`${API}/meetings`, meetingPayload());
        const id = r.data.meeting.id;
        for (const a of draft.actions) {
          await axios.post(`${API}/meetings/${id}/actions`, {
            description: a.description, owner_id: a.owner_id || null,
            owner_name: a.owner_name || null, due_date: a.due_date || null,
            status: a.status || "open",
            link_type: a.link_type || null, link_id: a.link_id || null, link_name: a.link_name || null,
          });
        }
        toast.success("Meeting created");
        setSelectedId(id);
        await openMeeting(id);
      }
      loadMeetings(); loadRegister(myOnly);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const removeMeeting = async () => {
    if (selectedId === "new") { setSelectedId(null); setDraft(null); return; }
    if (!window.confirm("Delete this meeting and its action items?")) return;
    await axios.delete(`${API}/meetings/${selectedId}`);
    toast.success("Meeting deleted");
    setSelectedId(null); setDraft(null); loadMeetings(); loadRegister(myOnly);
  };

  // ── action editing (local for unsaved, live API for saved) ──
  const userName = (id) => (users.find((u) => u.id === id) || {}).name;
  const patchLocalAction = (i, patch) =>
    setDraft((d) => ({ ...d, actions: d.actions.map((a, idx) => idx === i ? { ...a, ...patch } : a) }));

  const editAction = async (i, patch) => {
    const a = draft.actions[i];
    if (patch.owner_id !== undefined) patch.owner_name = userName(patch.owner_id) || null;
    patchLocalAction(i, patch);
    if (persisted && a.id) {
      await axios.put(`${API}/meetings/action/${a.id}`, patch);
      loadRegister(myOnly);
      loadMeetings();
    }
  };
  const addActionRow = () =>
    setDraft((d) => ({ ...d, actions: [...d.actions, { description: "", owner_id: null, owner_name: null, due_date: null, status: "open" }] }));
  const removeAction = async (i) => {
    const a = draft.actions[i];
    setDraft((d) => ({ ...d, actions: d.actions.filter((_, idx) => idx !== i) }));
    if (persisted && a.id) { await axios.delete(`${API}/meetings/action/${a.id}`); loadRegister(myOnly); loadMeetings(); }
  };
  // for a saved meeting, a freshly-added row needs to be persisted on blur of its description
  const commitNewAction = async (i) => {
    const a = draft.actions[i];
    if (!persisted || a.id || !a.description.trim()) return;
    const r = await axios.post(`${API}/meetings/${selectedId}/actions`, {
      description: a.description, owner_id: a.owner_id || null, owner_name: a.owner_name || null,
      due_date: a.due_date || null, status: a.status || "open",
      link_type: a.link_type || null, link_id: a.link_id || null, link_name: a.link_name || null,
    });
    patchLocalAction(i, { id: r.data.action.id });
    loadRegister(myOnly); loadMeetings();
  };

  const openLinkPicker = async (i) => {
    setLinkFor(i); setLinkQ(""); setLinkResults([]);
    const [cl, pr] = await Promise.all([
      axios.get(`${API}/customer-success/clients`).catch(() => ({ data: { clients: [] } })),
      axios.get(`${API}/sales/prospects`, { params: { limit: 50 } }).catch(() => ({ data: { prospects: [] } })),
    ]);
    const clients = (cl.data.clients || []).map((c) => ({ type: "client", id: c.client_id, name: c.company_name }));
    const prospects = (pr.data.prospects || pr.data.items || []).map((p) => ({ type: "prospect", id: p.id, name: p.company_name }));
    setLinkResults([...clients, ...prospects].filter((x) => x.id && x.name));
  };
  const pickLink = (opt) => { editAction(linkFor, { link_type: opt.type, link_id: opt.id, link_name: opt.name }); setLinkFor(null); };
  const unlink = (i) => editAction(i, { link_type: null, link_id: "", link_name: "" });

  const toggleRegisterStatus = async (a, status) => {
    await axios.put(`${API}/meetings/action/${a.id}`, { status });
    loadRegister(myOnly); loadMeetings();
    if (persisted) openMeeting(selectedId);
  };

  return (
    <div className="space-y-4" data-testid="meetings-view">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold text-[#1A1A1A] flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-[#8A6D00]" /> Management Meetings
          </h2>
          <p className="text-sm text-[#59554D] mt-1 max-w-2xl">
            Capture the key parts of your weekly meeting from the Teams AI note taker. Paste the
            notes, let AI pull out the summary, decisions, risks and action items, then track those
            actions to done so nothing slips through the cracks.
          </p>
        </div>
        <div className="flex gap-1 bg-[#FFFFFF] border border-[#DAD5CA] rounded-lg p-1">
          <button onClick={() => setTab("meetings")} data-testid="meetings-tab-meetings"
            className={`px-3 py-1.5 text-sm rounded-md ${tab === "meetings" ? "bg-[#FFD108] text-[#1A1A1A]" : "text-[#59554D]"}`}>Meetings</button>
          <button onClick={() => { setTab("register"); loadRegister(myOnly); }} data-testid="meetings-tab-register"
            className={`px-3 py-1.5 text-sm rounded-md flex items-center gap-1.5 ${tab === "register" ? "bg-[#FFD108] text-[#1A1A1A]" : "text-[#59554D]"}`}>
            Action Register
            {register.overdue > 0 && <span className="rounded-full bg-[#C23A00] px-1.5 text-[10px] text-[#1A1A1A]">{register.overdue}</span>}
          </button>
        </div>
      </div>

      {tab === "register" ? (
        <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="text-sm text-[#1A1A1A]">{register.total} open action{register.total === 1 ? "" : "s"}
                {register.overdue > 0 && <span className="text-[#C23A00] ml-2">· {register.overdue} overdue</span>}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => { const v = !myOnly; setMyOnly(v); loadRegister(v); }}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors ${myOnly ? "border-[#8A6D00] bg-[#FFF8D6] text-[#8A6D00]" : "border-[#DAD5CA] bg-[#FFFFFF] text-[#59554D] hover:text-[#1A1A1A]"}`}
                  data-testid="register-my-actions">
                  <UserIcon className="w-3 h-3" /> My actions
                </button>
                <Button size="sm" variant="outline" onClick={runNudge} disabled={nudging}
                  className="h-7 border-[#DAD5CA] text-[#59554D] hover:text-[#1A1A1A]" data-testid="register-nudge">
                  {nudging ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />}
                  <span className="ml-1">Email overdue owners</span>
                </Button>
              </div>
            </div>
            {register.actions.length === 0 ? (
              <div className="text-sm text-[#8A857A] py-8 text-center">No open actions — nice and clear.</div>
            ) : (
              <div className="space-y-1.5" data-testid="register-list">
                {register.actions.map((a) => (
                  <div key={a.id} className={`flex items-center justify-between gap-3 rounded border px-3 py-2 ${a.overdue ? "border-[#FFE9E0] bg-[#FFE9E0]" : "border-[#DAD5CA] bg-[#FFFFFF]"}`} data-testid={`register-action-${a.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-[#1A1A1A] truncate">{a.description}</div>
                      <div className="text-xs text-[#8A857A] flex items-center gap-2 mt-0.5">
                        <Users className="w-3 h-3" />{a.owner_name || "Unassigned"}
                        {a.due_date && <span className={a.overdue ? "text-[#C23A00]" : ""}><Flag className="w-3 h-3 inline mr-0.5" />{formatDate(a.due_date)}{a.overdue ? " · overdue" : ""}</span>}
                        <span className="text-[#8A857A]">· from {formatDate(a.meeting_date)}</span>
                      </div>
                    </div>
                    <Select value={a.status} onValueChange={(v) => toggleRegisterStatus(a, v)}>
                      <SelectTrigger className="w-32 h-8 bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] text-xs shrink-0" data-testid={`register-status-${a.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A]">
                        {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Meeting list */}
          <div className="space-y-2">
            <Button size="sm" onClick={newMeeting} className="w-full bg-[#FFD108] text-[#1A1A1A] hover:bg-[#0070e0]" data-testid="meetings-new">
              <Plus className="w-4 h-4 mr-2" /> New meeting
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setTemplateText((agenda.length ? agenda : []).join("\n")); setTemplateOpen(true); }}
              className="w-full border-[#DAD5CA] text-[#59554D] hover:text-[#1A1A1A]" data-testid="meetings-template-btn">
              <FileText className="w-4 h-4 mr-2" /> Agenda template
            </Button>
            {loading && <div className="text-[#8A857A] text-sm py-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>}
            {!loading && meetings.length === 0 && <div className="text-xs text-[#8A857A] text-center py-6">No meetings recorded yet.</div>}
            {meetings.map((m) => (
              <Card key={m.id} onClick={() => openMeeting(m.id)}
                className={`bg-[#FFFFFF] border cursor-pointer transition-colors ${selectedId === m.id ? "border-[#8A6D00]" : "border-[#DAD5CA] hover:border-[#DAD5CA]"}`}
                data-testid={`meeting-card-${m.id}`}>
                <CardContent className="p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-[#1A1A1A] truncate">{m.title}</div>
                      <div className="text-xs text-[#8A857A] flex items-center gap-1 mt-0.5"><CalendarDays className="w-3 h-3" />{formatDate(m.meeting_date)}</div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {m.open_action_count > 0 && <Badge className="bg-[#FFF7D1] text-[#6B5600] border-0 text-[10px]"><ListChecks className="w-3 h-3 mr-0.5" />{m.open_action_count}</Badge>}
                      <ChevronRight className="w-4 h-4 text-[#8A857A]" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Editor */}
          <div className="lg:col-span-2">
            {!draft ? (
              <Card className="bg-[#FFFFFF] border-[#DAD5CA] h-full"><CardContent className="p-10 text-center text-sm text-[#8A857A]">Select a meeting or create a new one.</CardContent></Card>
            ) : (
              <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                <CardContent className="p-5 space-y-4">
                  <div className="grid grid-cols-3 gap-3">
                    <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                      placeholder="Meeting title (optional)" className="col-span-2 bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A]" data-testid="meeting-title" />
                    <Input type="date" value={draft.meeting_date} onChange={(e) => setDraft({ ...draft, meeting_date: e.target.value })}
                      className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A]" data-testid="meeting-date" />
                  </div>

                  {/* Paste + extract */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs uppercase tracking-wider text-[#8A857A]">AI note-taker text</label>
                      <Button size="sm" variant="ghost" onClick={extract} disabled={extracting}
                        className="h-7 text-[#B794F6] hover:text-[#D6BCFA]" data-testid="meeting-extract">
                        {extracting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                        <span className="ml-1 text-xs">Extract with AI</span>
                      </Button>
                    </div>
                    <Textarea value={draft.raw_notes} onChange={(e) => setDraft({ ...draft, raw_notes: e.target.value })}
                      placeholder="Paste the Teams AI note-taker summary/transcript here…" rows={5}
                      className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] text-sm" data-testid="meeting-notes" />
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-wider text-[#8A857A]">Summary</label>
                    <Textarea value={draft.summary} onChange={(e) => setDraft({ ...draft, summary: e.target.value })}
                      rows={2} className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] text-sm mt-1" data-testid="meeting-summary" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs uppercase tracking-wider text-[#0E6B3F]">Key decisions <span className="text-[#8A857A] normal-case">(one per line)</span></label>
                      <Textarea value={draft.decisionsText} onChange={(e) => setDraft({ ...draft, decisionsText: e.target.value })}
                        rows={4} className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] text-sm mt-1" data-testid="meeting-decisions" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-wider text-[#6B5600]">Risks / watch <span className="text-[#8A857A] normal-case">(one per line)</span></label>
                      <Textarea value={draft.risksText} onChange={(e) => setDraft({ ...draft, risksText: e.target.value })}
                        rows={4} className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] text-sm mt-1" data-testid="meeting-risks" />
                    </div>
                  </div>

                  {/* Action items */}
                  <div className="border-t border-[#DAD5CA] pt-3">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-[#1A1A1A] flex items-center gap-1.5"><ListChecks className="w-4 h-4 text-[#8A6D00]" /> Action items</label>
                      <Button size="sm" variant="outline" onClick={addActionRow} className="h-7 border-[#DAD5CA] text-[#59554D]" data-testid="meeting-add-action"><Plus className="w-3.5 h-3.5 mr-1" />Add</Button>
                    </div>
                    {draft.actions.length === 0 && <div className="text-xs text-[#8A857A] py-2">No action items yet — add one, or extract from the notes.</div>}
                    <div className="space-y-2">
                      {draft.actions.map((a, i) => (
                        <div key={a.id || i} data-testid={`meeting-action-${i}`}>
                          <div className="grid grid-cols-12 gap-2 items-center">
                            <Input value={a.description} onChange={(e) => patchLocalAction(i, { description: e.target.value })} onBlur={() => commitNewAction(i)}
                              placeholder="Action…" className="col-span-4 h-8 bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] text-xs" data-testid={`meeting-action-desc-${i}`} />
                            <Select value={a.owner_id || "none"} onValueChange={(v) => editAction(i, { owner_id: v === "none" ? null : v })}>
                              <SelectTrigger className="col-span-3 h-8 bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] text-xs" data-testid={`meeting-action-owner-${i}`}><SelectValue placeholder="Owner" /></SelectTrigger>
                              <SelectContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-h-60">
                                <SelectItem value="none">Unassigned</SelectItem>
                                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            <Input type="date" value={a.due_date || ""} onChange={(e) => editAction(i, { due_date: e.target.value || null })}
                              className="col-span-2 h-8 bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] text-xs px-1" data-testid={`meeting-action-due-${i}`} />
                            <Select value={a.status} onValueChange={(v) => editAction(i, { status: v })}>
                              <SelectTrigger className="col-span-1 h-8 bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] text-[10px] px-1" data-testid={`meeting-action-status-${i}`}><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A]">
                                {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            <Button variant="ghost" size="icon" onClick={() => openLinkPicker(i)} title="Link to a client / prospect"
                              className={`col-span-1 h-7 w-7 ${a.link_id ? "text-[#8A6D00]" : "text-[#8A857A] hover:text-[#1A1A1A]"}`} data-testid={`meeting-action-link-${i}`}>
                              <Link2 className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => removeAction(i)} className="col-span-1 h-7 w-7 text-[#8A857A] hover:text-[#C23A00]"><X className="w-3.5 h-3.5" /></Button>
                          </div>
                          {a.link_id && (
                            <div className="text-[11px] text-[#8A6D00] pl-1 mt-0.5 flex items-center gap-1" data-testid={`meeting-action-linked-${i}`}>
                              <Link2 className="w-3 h-3" /> {a.link_type}:{" "}
                              {recordHref(a.link_type, a.link_id) ? (
                                <a href={recordHref(a.link_type, a.link_id)} target="_blank" rel="noreferrer"
                                  className="underline hover:no-underline" title={`Open ${a.link_name}`}
                                  data-testid={`meeting-action-record-${i}`}>{a.link_name}</a>
                              ) : a.link_name}
                              <button onClick={() => unlink(i)} className="text-[#8A857A] hover:text-[#C23A00] ml-1">×</button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-1">
                    <Button variant="ghost" size="sm" onClick={removeMeeting} className="text-[#C23A00] hover:text-[#ff6b62]" data-testid="meeting-delete"><Trash2 className="w-4 h-4 mr-2" />Delete</Button>
                    <Button size="sm" onClick={save} disabled={saving} className="bg-[#FFD108] text-[#1A1A1A] hover:bg-[#0070e0]" data-testid="meeting-save">
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save meeting
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      <Dialog open={templateOpen} onOpenChange={setTemplateOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-md" data-testid="meetings-template-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText className="w-4 h-4 text-[#8A6D00]" /> Standing agenda template</DialogTitle></DialogHeader>
          <p className="text-xs text-[#59554D] -mt-2">One recurring topic per line. New meetings pre-fill their notes with this agenda so nothing standing gets missed.</p>
          <Textarea value={templateText} onChange={(e) => setTemplateText(e.target.value)} rows={8}
            placeholder={"Wins since last week\nSales pipeline & demos\nRisks & blockers\nReview of previous action items"}
            className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] text-sm" data-testid="meetings-template-text" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTemplateOpen(false)} className="text-[#59554D]">Cancel</Button>
            <Button onClick={saveTemplate} className="bg-[#FFD108] text-[#1A1A1A] hover:bg-[#0070e0]" data-testid="meetings-template-save">Save template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={linkFor !== null} onOpenChange={(o) => !o && setLinkFor(null)}>
        <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-md" data-testid="meeting-link-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Link2 className="w-4 h-4 text-[#8A6D00]" /> Link action to a client / prospect</DialogTitle></DialogHeader>
          <Input value={linkQ} onChange={(e) => setLinkQ(e.target.value)} placeholder="Search by company…"
            className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A]" data-testid="meeting-link-search" autoFocus />
          <div className="max-h-72 overflow-y-auto space-y-1">
            {linkResults.filter((o) => o.name.toLowerCase().includes(linkQ.toLowerCase())).slice(0, 40).map((o) => (
              <button key={`${o.type}-${o.id}`} onClick={() => pickLink(o)}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded border border-[#DAD5CA] bg-[#FFFFFF] hover:border-[#FFF8D6] text-left"
                data-testid={`meeting-link-opt-${o.id}`}>
                <span className="text-sm text-[#1A1A1A] truncate">{o.name}</span>
                <Badge className={`text-[10px] border-0 ${o.type === "client" ? "bg-[#E3F3E9] text-[#0E6B3F]" : "bg-[#FFF8D6] text-[#8A6D00]"}`}>{o.type}</Badge>
              </button>
            ))}
            {linkResults.length === 0 && <div className="text-xs text-[#8A857A] py-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>}
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setLinkFor(null)} className="text-[#59554D]">Cancel</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
